import type { IChallenge, IObjective } from "@bbj-types/challenge";
import { array, mixed, object, string } from "yup";
import { formatDefautValues, resolver as objectiveResolver } from "../objective/logics";
import type { TPaperCandidateChallengeChallenge, TPaperCandidateChallengeObjective } from "../types";

export interface IFormEditCandidateChallengeObjective<CandidateObjective extends TPaperCandidateChallengeObjective> {
  source?: CandidateObjective;
  objective: IObjective | null;
  objectiveTitle: string;
  objectiveEndDate: Date | null;
}

export interface IFormEditCandidateChallenge<CandidateObjective extends TPaperCandidateChallengeObjective> {
  description: string;
  objectives: IFormEditCandidateChallengeObjective<CandidateObjective>[];
}

export interface IResolverEditCandidateChallenge<CandidateObjective extends TPaperCandidateChallengeObjective> {
  description: string;
  objectives: (Omit<IFormEditCandidateChallengeObjective<CandidateObjective>, "objective"> & {
    objective: IObjective;
  })[];
}

export const resolver = (timeZone: string) =>
  object({
    description: string(),
    objectives: array(objectiveResolver(timeZone).shape({ source: mixed() })),
  });

export const emptyObjective = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
>(): IFormEditCandidateChallengeObjective<CandidateObjective> => ({
  source: undefined,
  ...formatDefautValues({ objectives: [] }, undefined),
});

export const formatDefaultValues = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
>(
  challenge: Pick<IChallenge, "objectives">,
  candidateChallenge: CandidateChallenge
): IFormEditCandidateChallenge<CandidateObjective> => ({
  description: candidateChallenge.description ?? "",
  objectives: candidateChallenge.objectives.map((candidateObjective) => ({
    source: candidateObjective,
    ...formatDefautValues(challenge, candidateObjective),
  })),
});

export const formatSubmitValues = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
>(
  candidateChallenge: CandidateChallenge,
  data: IResolverEditCandidateChallenge<CandidateObjective>
): CandidateChallenge => ({
  ...candidateChallenge,
  description: data.description,
  objectives: data.objectives.map(
    ({ source, objective, objectiveTitle, objectiveEndDate }) =>
      ({
        ...source,
        objective: objective._id,
        label: objectiveTitle || undefined,
        deadline: objectiveEndDate ?? undefined,
      }) as CandidateObjective
  ),
});
